import { z } from 'zod';
import {
  PoCompletionSchema,
  PurchaseOrderSchema,
  isPoEditable,
  type PoCompletion,
  type PurchaseOrder,
} from '../../domain/entities/purchase-order.ts';
import { CostLineSchema, type CostLine } from '../../domain/entities/cost-line.ts';
import { newId } from '../../domain/id.ts';
import type { FunctionsPort } from '../../ports/functions-port.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Writing purchase orders, and nothing that changes their state.
 *
 * Creating one and moving it between states are callables
 * (`createPurchaseOrder`, `transitionPurchaseOrder`), for the two reasons
 * change orders have: the **number** is sequential per job, so it has to be
 * allocated where nothing can interleave, and **releasing** is a promise to
 * a supplier — a client that could write `status` could commit the
 * company's money without anybody's permission.
 *
 * What is here is what a draft's own author does: the title, the scope, the
 * lines, who it is for.
 */
export class PurchaseOrderError extends Error {
  readonly reason: 'invalid' | 'notEditable';

  constructor(reason: 'invalid' | 'notEditable', message: string) {
    super(message);
    this.name = 'PurchaseOrderError';
    this.reason = reason;
  }
}

export const SavePurchaseOrderInputSchema = z.object({
  title: z.string().trim().min(1, 'A purchase order needs a title').max(200),
  assignedContactId: z.string().min(1).optional(),
  materialsOnly: z.boolean().optional(),
  scope: z.string().max(20_000).optional(),
  lines: z.array(CostLineSchema).optional(),
  scheduledCompletion: PoCompletionSchema.nullable().optional(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type SavePurchaseOrderInput = z.input<typeof SavePurchaseOrderInputSchema>;

export interface PurchaseOrderContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

type ReadContext = Pick<PurchaseOrderContext, 'data' | 'companyId' | 'jobId'>;

function repo(context: ReadContext) {
  return context.data.collection<PurchaseOrder>(
    { companyId: context.companyId, jobId: context.jobId },
    'purchaseOrders',
  );
}

/**
 * A job's purchase orders, newest number first.
 *
 * Ordered by the `number` **string**, which works because the sequence is
 * zero-padded to four — see `formatPoNumber`. An unpadded number would
 * sort `'SFM-12'` after `'SFM-0100'`.
 */
export async function listPurchaseOrders(context: ReadContext): Promise<PurchaseOrder[]> {
  const page = await repo(context).list({
    orderBy: [{ field: 'number', direction: 'desc' }],
  });
  return [...page.items];
}

export function subscribeToPurchaseOrders(
  context: ReadContext,
  onChange: (orders: readonly PurchaseOrder[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'number', direction: 'desc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function savePurchaseOrder(
  context: PurchaseOrderContext,
  id: string,
  input: SavePurchaseOrderInput,
): Promise<PurchaseOrder> {
  const parsed = SavePurchaseOrderInputSchema.safeParse(input);
  if (!parsed.success) throw new PurchaseOrderError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('purchaseOrders', id);
  if (!isPoEditable(existing.status)) {
    throw new PurchaseOrderError(
      'notEditable',
      `A ${existing.status} purchase order cannot be changed. Unrelease it first.`,
    );
  }

  const patch = {
    title: parsed.data.title,
    ...(parsed.data.assignedContactId !== undefined
      ? { assignedContactId: parsed.data.assignedContactId }
      : {}),
    materialsOnly: parsed.data.materialsOnly ?? existing.materialsOnly,
    scope: parsed.data.scope ?? existing.scope,
    lines: parsed.data.lines ?? existing.lines,
    // `null` clears it, `undefined` leaves it: a PO can stop following a
    // schedule item, and without the distinction there would be no way to
    // say so — the same shape the schedule's own link editor uses.
    ...(parsed.data.scheduledCompletion === null
      ? { scheduledCompletion: undefined }
      : parsed.data.scheduledCompletion !== undefined
        ? { scheduledCompletion: parsed.data.scheduledCompletion as PoCompletion }
        : {}),
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = PurchaseOrderSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new PurchaseOrderError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'purchaseOrders' as const,
      id,
      patch,
      // The version it read. Two people editing one PO is realistic — a
      // buyer and the PM who asked for it — and last-write-wins would lose
      // a scope somebody typed.
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/**
 * Replace one line, keeping the rest.
 *
 * Pure, like the estimate's. The caller hands the result to
 * `savePurchaseOrder`, so one write carries the whole document and there
 * is no window where the lines and the totals disagree.
 */
export function withPoLineReplaced(
  order: Pick<PurchaseOrder, 'lines'>,
  line: CostLine,
): CostLine[] {
  const existing = order.lines.findIndex((candidate) => candidate.id === line.id);
  if (existing === -1) return [...order.lines, line];
  return order.lines.map((candidate) => (candidate.id === line.id ? line : candidate));
}

export function withPoLineRemoved(order: Pick<PurchaseOrder, 'lines'>, lineId: string): CostLine[] {
  return order.lines.filter((line) => line.id !== lineId);
}

// ---- the three callables, wrapped ---------------------------------------
//
// Thin on purpose: the port is typed by `FunctionCatalog`, so these add an
// id and a name rather than any logic. What they buy is that a page never
// types a callable's name — a typo there is a runtime failure that looks
// like a CORS error, which is the failure `callables.ts` exists to prevent.

/**
 * Raise a purchase order and get its number.
 *
 * The id is generated here unless the caller supplies one, which is what
 * makes a retry safe: the same id is the same purchase order, so a dropped
 * response and a second attempt converge on one document with one number.
 */
export async function createPurchaseOrder(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly title: string;
    readonly assignedContactId?: string;
    readonly scope?: string;
    readonly materialsOnly?: boolean;
    /** Supplied by a caller that is retrying; generated otherwise. */
    readonly purchaseOrderId?: string;
    /**
     * The awarded bid package this comes out of, when it comes out of
     * one. Only the id travels: the server reads the package itself for
     * the provenance it writes, so an order cannot claim bids it was
     * never part of.
     */
    readonly sourceBidPackageId?: string;
  },
): Promise<{ readonly purchaseOrderId: string; readonly number: string }> {
  const purchaseOrderId = input.purchaseOrderId ?? newId();
  return functions.call('createPurchaseOrder', {
    companyId: input.companyId,
    jobId: input.jobId,
    purchaseOrderId,
    title: input.title,
    ...(input.assignedContactId === undefined
      ? {}
      : { assignedContactId: input.assignedContactId }),
    ...(input.scope === undefined ? {} : { scope: input.scope }),
    ...(input.materialsOnly === undefined ? {} : { materialsOnly: input.materialsOnly }),
    ...(input.sourceBidPackageId === undefined
      ? {}
      : { sourceBidPackageId: input.sourceBidPackageId }),
  });
}

export async function transitionPurchaseOrder(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly purchaseOrderId: string;
    readonly to: 'released' | 'approved' | 'unreleased' | 'void';
    readonly reason?: string;
  },
): Promise<{ readonly status: string }> {
  return functions.call('transitionPurchaseOrder', input);
}

export async function setPurchaseOrderWorkComplete(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly purchaseOrderId: string;
    readonly complete: boolean;
  },
): Promise<{ readonly complete: boolean }> {
  return functions.call('setPurchaseOrderWorkComplete', input);
}
